import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Landing page hit from the "Verify email" link inside the transactional
 * Resend email. Reads `?token=…`, POSTs to `/api/auth/verify-email`, then
 * refreshes the auth context so the trading gate lifts immediately if the
 * user is already logged in on this device. Also handles the un-authed case
 * (user clicked the link from a different browser) by asking them to log in.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | success | already | error
  const [errorMsg, setErrorMsg] = useState('');
  const token = params.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('This link is missing its verification token. Please use the link from your email exactly as sent.');
      return;
    }
    (async () => {
      try {
        const { data } = await api.post('/auth/verify-email', { token });
        setStatus(data.already_verified ? 'already' : 'success');
        // If user is authenticated on this device, refresh their session so
        // the /trade guards lift right away.
        try { await refreshMe(); } catch { /* noop */ }
      } catch (e) {
        setStatus('error');
        setErrorMsg(e?.response?.data?.detail || 'We couldn\'t verify this link. It may have expired.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="verify-page" data-testid="verify-email-page">
      <div className="verify-card">
        {status === 'loading' && (
          <>
            <div className="verify-hero verify-spin" aria-hidden="true">📮</div>
            <h1 className="verify-title">Verifying your email…</h1>
            <p className="verify-copy">Hang tight, this should only take a second.</p>
          </>
        )}

        {(status === 'success' || status === 'already') && (
          <>
            <div className="verify-hero" aria-hidden="true">✅</div>
            <h1 className="verify-title" data-testid="verify-email-success">
              {status === 'success' ? 'Email verified!' : 'You\'re already verified'}
            </h1>
            <p className="verify-copy">
              {status === 'success'
                ? 'Your account is active. Time to back some Clans and get in on those launches.'
                : 'Nothing more to do — you\'re good to trade.'}
            </p>
            {user
              ? (
                <button
                  className="verify-cta"
                  data-testid="verify-continue-btn"
                  onClick={() => navigate('/home')}
                >Continue to Clanster →</button>
              )
              : (
                <Link
                  to="/login"
                  className="verify-cta"
                  data-testid="verify-login-link"
                >Log in →</Link>
              )
            }
          </>
        )}

        {status === 'error' && (
          <>
            <div className="verify-hero" aria-hidden="true">⚠️</div>
            <h1 className="verify-title" data-testid="verify-email-error">Verification failed</h1>
            <p className="verify-copy">{errorMsg}</p>
            {user
              ? (
                <button
                  className="verify-cta secondary"
                  data-testid="verify-back-btn"
                  onClick={() => navigate('/home')}
                >Back to Clanster</button>
              )
              : (
                <Link
                  to="/login"
                  className="verify-cta secondary"
                  data-testid="verify-login-link"
                >Log in to resend</Link>
              )
            }
          </>
        )}
      </div>
    </div>
  );
}
